// SPDX-License-Identifier: Elastic-2.0
/**
 * zigbeeMock — MSW handlers for the F-353 Zigbee pairing flow.
 *
 * Provides a *stateful* mock so the Zigbee pairing flow (and T-004 tests)
 * can drive a single job from STARTING through IN_PROGRESS to a terminal
 * state across successive polls:
 *
 *   POST /onboarding/zigbee/permit → STARTING job (fixed job_id)
 *   GET  /onboarding/jobs/{id}     → advances on every call:
 *                                    starting → in_progress → succeeded
 *   DELETE /onboarding/jobs/{id}   → cancelled
 *
 * URL patterns use regex on the host (like matterMock / onboardingMock) so
 * they match both http://localhost:8200/api/v1/onboarding/* (vitest) and
 * /api/mirlai/v1/onboarding/* (single-host add-on).
 *
 * ── Ordering vs. the F-351 onboardingMock ─────────────────────────────────
 * onboardingMock.ts ALSO registers a generic `GET /onboarding/jobs/{id}`
 * handler (and a DELETE). MSW resolves with the FIRST matching handler, so
 * `zigbeeHandlers` MUST be registered BEFORE `onboardingHandlers` for the
 * stateful Zigbee machine to win in Zigbee-flow tests/dev. To make this
 * robust regardless of registration order, the GET/DELETE handlers here
 * *only* respond for the Zigbee job id (ZIGBEE_JOB_ID) and otherwise return
 * `passthrough()`, letting the generic F-351 handler take over for any other
 * job id. So even if someone wires them in the "wrong" order, the zigbee job
 * still resolves to the stateful mock and non-zigbee jobs fall through.
 */
import { HttpResponse, http, passthrough } from "msw";

import type { CommissioningJob } from "@/api/onboardingClient";

const ZIGBEE_PERMIT_RE = /\/onboarding\/zigbee\/permit$/;
const JOB_RE = /\/onboarding\/jobs\/([^/]+)$/;

/** Fixed job id the Zigbee permit POST always returns. */
export const ZIGBEE_JOB_ID = "zigbee-job-1";

/** Forced terminal outcomes a test can request before polling. */
type ForcedOutcome = "failed" | "timed_out";

/** Special markers the POST recognises to force a failure outcome. */
const FORCE_FAILED_CODE = "FORCE-FAILED";
const FORCE_TIMEOUT_CODE = "FORCE-TIMEOUT";

/**
 * Stateful poll counter per job id. Each GET increments it so the same job
 * advances through its lifecycle. Keyed by job_id so multiple jobs could be
 * tracked independently.
 */
const pollCounts = new Map<string, number>();

/** Forced outcome per job id, set by the POST (special marker) or a test. */
const forcedOutcomes = new Map<string, ForcedOutcome>();

const STARTED_AT = "2026-06-01T12:00:00Z";

const NETWORK_OPEN_EVENT =
  "Network open — put your device in pairing mode…";

function baseJob(): CommissioningJob {
  return {
    job_id: ZIGBEE_JOB_ID,
    kind: "zigbee",
    state: "starting",
    progress: 0.0,
    code_kind: null,
    device_ref: null,
    error: null,
    events: ["Starting…"],
    started_at: STARTED_AT,
    updated_at: STARTED_AT,
  };
}

/**
 * Reset all mock state. Call in `beforeEach`/`afterEach` so each test starts
 * with a fresh STARTING job and no forced outcome.
 */
export function resetZigbeeMock(): void {
  pollCounts.clear();
  forcedOutcomes.clear();
}

/**
 * Force the zigbee job to a terminal failure outcome on its next terminal
 * poll. Call before polling to exercise the error/timeout UI.
 */
export function forceZigbeeOutcome(
  outcome: ForcedOutcome,
  jobId: string = ZIGBEE_JOB_ID,
): void {
  forcedOutcomes.set(jobId, outcome);
}

/** Compute the job snapshot for the Nth poll (0-indexed). */
function jobForPoll(jobId: string, poll: number): CommissioningJob {
  const job = baseJob();
  job.job_id = jobId;

  // Poll 0: STARTING (as just created).
  if (poll <= 0) {
    return job;
  }

  // Poll 1: IN_PROGRESS at 40% — network open, awaiting the device to join.
  if (poll === 1) {
    return {
      ...job,
      state: "in_progress",
      progress: 0.4,
      events: ["Starting…", NETWORK_OPEN_EVENT],
    };
  }

  // Poll >=2: terminal. Honour a forced failure/timeout if requested.
  const forced = forcedOutcomes.get(jobId);
  if (forced === "failed") {
    return {
      ...job,
      state: "failed",
      progress: 0.4,
      error: "Pairing failed",
      events: ["Starting…", NETWORK_OPEN_EVENT, "Failed"],
    };
  }
  if (forced === "timed_out") {
    return {
      ...job,
      state: "timed_out",
      progress: 0.4,
      error: "Pairing timed out",
      events: ["Starting…", NETWORK_OPEN_EVENT, "Timed out"],
    };
  }

  return {
    ...job,
    state: "succeeded",
    progress: 1.0,
    device_ref: "sensor.zigbee_test",
    events: ["Starting…", NETWORK_OPEN_EVENT, "Paired"],
  };
}

/**
 * MSW handler set for the F-353 Zigbee pairing endpoints.
 *
 * Register BEFORE `onboardingHandlers` (see file header) — though the GET /
 * DELETE handlers passthrough for non-zigbee job ids so the generic F-351
 * handler still works for those.
 */
export const zigbeeHandlers = [
  http.post(ZIGBEE_PERMIT_RE, async ({ request }) => {
    let qrCode = "";
    try {
      const body = (await request.json()) as {
        qr_code?: string | null;
      };
      qrCode = body.qr_code ?? "";
    } catch {
      // No/invalid JSON body — treat as empty code.
    }

    // Reset poll state for a fresh job and apply any forced outcome encoded
    // in the QR payload so tests can drive a failure end-to-end.
    pollCounts.set(ZIGBEE_JOB_ID, 0);
    if (qrCode === FORCE_FAILED_CODE) {
      forcedOutcomes.set(ZIGBEE_JOB_ID, "failed");
    } else if (qrCode === FORCE_TIMEOUT_CODE) {
      forcedOutcomes.set(ZIGBEE_JOB_ID, "timed_out");
    }

    return HttpResponse.json(baseJob());
  }),

  http.get(JOB_RE, ({ request }) => {
    const path = new URL(request.url).pathname;
    const jobId = path.split("/").filter(Boolean).pop() ?? "";

    // Only the stateful Zigbee job is owned here; everything else falls
    // through to the generic F-351 handler.
    if (jobId !== ZIGBEE_JOB_ID) {
      return passthrough();
    }

    const poll = pollCounts.get(jobId) ?? 0;
    const snapshot = jobForPoll(jobId, poll);
    pollCounts.set(jobId, poll + 1);
    return HttpResponse.json(snapshot);
  }),

  http.delete(JOB_RE, ({ request }) => {
    const path = new URL(request.url).pathname;
    const jobId = path.split("/").filter(Boolean).pop() ?? "";

    if (jobId !== ZIGBEE_JOB_ID) {
      return passthrough();
    }

    return HttpResponse.json({
      ...baseJob(),
      state: "cancelled",
      events: ["Starting…", "Cancelled"],
    });
  }),
];
